import React, { useMemo } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TeamRankingSection from './TeamRankingSection.jsx';
import './RankingWidget.css';

const TEAM_RANKING_TABS = {
  rugby: [
    { key: 'best_attack', title: 'POINTS MARQUÉS', shortLabel: 'Pts +' },
    { key: 'best_attack_home', title: 'POINTS MARQUÉS DOMICILE', shortLabel: 'Pts + dom.' },
    { key: 'best_attack_away', title: 'POINTS MARQUÉS EXTÉRIEUR', shortLabel: 'Pts + ext.' },
    { key: 'best_defense', title: 'POINTS ENCAISSÉS', shortLabel: 'Pts -' },
    { key: 'best_defense_home', title: 'POINTS ENCAISSÉS DOMICILE', shortLabel: 'Pts - dom.' },
    { key: 'best_defense_away', title: 'POINTS ENCAISSÉS EXTÉRIEUR', shortLabel: 'Pts - ext.' },
    { key: 'best_try_scorers', title: 'ESSAIS MARQUÉS', shortLabel: 'Essais +' },
    { key: 'best_try_defenders', title: 'ESSAIS ENCAISSÉS', shortLabel: 'Essais -' },
  ],
  soccer: [
    { key: 'best_attack', title: 'MEILLEURE ATTAQUE', shortLabel: 'Attaque' },
    { key: 'best_attack_home', title: 'ATTAQUE DOMICILE', shortLabel: 'Dom.' },
    { key: 'best_attack_away', title: 'ATTAQUE EXTÉRIEUR', shortLabel: 'Ext.' },
    { key: 'best_defense', title: 'MEILLEURE DÉFENSE', shortLabel: 'Défense' },
    { key: 'best_defense_home', title: 'DÉFENSE DOMICILE', shortLabel: 'Déf. dom.' },
    { key: 'best_defense_away', title: 'DÉFENSE EXTÉRIEUR', shortLabel: 'Déf. ext.' },
  ],
};

const getTabIndexFromStatType = (tabs, statType) => {
  const index = tabs.findIndex((tab) => tab.key === statType);
  return index === -1 ? 0 : index;
};

const getDefaultSortByStatType = (statType) => {
  switch (statType) {
    case 'best_defense':
    case 'best_defense_home':
    case 'best_defense_away':
    case 'best_try_defenders':
      return 'value_asc';
    case 'best_attack':
    case 'best_attack_home':
    case 'best_attack_away':
    case 'best_try_scorers':
    default:
      return 'value_desc';
  }
};

const normalizeSeasonLabel = (season) => {
  const value = String(season || '').trim();
  return value ? `Saison ${value}` : '';
};

export default function TeamRankingWidget({
  discipline = 'rugby',
  competitionIds = [],
  season,
  onNavigate,
  widgetConfigurationId = null,
  title,
  showCompetitionInfos = true,
  defaultLimit = 20,
  limitOptions = [10, 20, 50, 100],
  statType = '',
  onStatTypeChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabs = useMemo(() => TEAM_RANKING_TABS[discipline] || [], [discipline]);
  const activeTab = getTabIndexFromStatType(tabs, statType);
  const currentTab = tabs[activeTab];
  const currentSort = getDefaultSortByStatType(currentTab?.key);
  const seasonLabel = normalizeSeasonLabel(season);
  const shouldShowTitleHeader = Boolean(title && showCompetitionInfos);

  const handleTabChange = (_, value) => {
    const nextStatType = tabs[value]?.key;
    if (!nextStatType) return;

    onStatTypeChange?.(nextStatType, {
      isDefaultTab: value === 0,
    });
  };

  if (!tabs.length) return null;

  return (
    <Box component="section" className="team-ranking-widget">
      <Box className="team-ranking-widget__container">
        <Box className="team-ranking-widget__stack">
          {shouldShowTitleHeader ? (
            <Box className="team-ranking-widget__header">
              <Box className="team-ranking-widget__title-wrap">
                <Box component="span" aria-hidden="true" className="team-ranking-widget__title-dot" />
                <Typography component="h2" className="team-ranking-widget__title" noWrap>
                  {title}
                </Typography>
              </Box>

              {season ? (
                <Typography className="team-ranking-widget__season">
                  {String(season).split('/')[0]}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {shouldShowTitleHeader ? (
            <Box className="team-ranking-widget__competition-band">
              <Typography className="team-ranking-widget__competition-title" noWrap>
                {title}
              </Typography>

              {seasonLabel ? (
                <Typography className="team-ranking-widget__competition-season">{seasonLabel}</Typography>
              ) : null}
            </Box>
          ) : null}

          <Box className="team-ranking-widget__tabs-wrap">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Types de statistiques équipes"
              className="team-ranking-widget__tabs"
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.key}
                  disableRipple
                  label={isMobile ? tab.shortLabel : tab.title}
                  className="team-ranking-widget__tab"
                />
              ))}
            </Tabs>
          </Box>

          {currentTab ? (
            <TeamRankingSection
              statType={currentTab.key}
              discipline={discipline}
              competitionIds={competitionIds}
              season={season}
              widgetConfigurationId={widgetConfigurationId}
              limit={defaultLimit}
              limitOptions={limitOptions}
              sort={currentSort}
              onNavigate={onNavigate}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
